import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { Trans, useTranslation } from 'react-i18next'
import { SIGNUP } from '../../routes/routeConstant'
import { FRIEND_TRIAL_DAYS, normalizeReferralCode, storeReferralCode } from '../../utils/referral'
import { checkReferralCode } from '../../redux/actions/referralAction'
import './invite-friends.css'

// What the 14 days include, as the all-in-one daily app: read, write and speak. The teacher is
// part of Pro, which the friend picks at the trial step (the 14 days apply to either plan).
const PERKS = [
  { emoji: '📖', key: 'referral:landing.perks.read' },
  { emoji: '✍️', key: 'referral:landing.perks.write' },
  { emoji: '🗣️', key: 'referral:landing.perks.speak' },
  { emoji: '🔥', key: 'referral:landing.perks.daily' },
]

const BRAND = 'maayot'

/**
 * /r/:code, the link a subscriber shares. Checks the code, remembers it for the signup call, and
 * sends the friend into the normal signup. A dead or mistyped code goes straight to signup with
 * no promise made, so nobody is shown a 14-day offer the checkout will not honour.
 */
const ReferralLanding: React.FC = () => {
  const { t } = useTranslation('referral')
  const { code: rawCode } = useParams()
  const navigate = useNavigate()
  const code = normalizeReferralCode(rawCode)
  const [state, setState] = useState<'checking' | 'valid' | 'invalid'>(code ? 'checking' : 'invalid')

  useEffect(() => {
    if (!code) return
    let alive = true
    checkReferralCode(code).then((valid) => {
      if (!alive) return
      try {
        posthog.capture('referral_landing_visited', { referral_code: code, valid })
        // Carried by sign_up and every later event in this visit.
        if (valid) posthog.register({ referral_code: code })
      } catch {
        /* analytics must never break the page */
      }
      if (valid) storeReferralCode(code)
      setState(valid ? 'valid' : 'invalid')
    })
    return () => {
      alive = false
    }
  }, [code])

  if (state === 'invalid') return <Navigate to={SIGNUP} replace />
  if (state === 'checking') return null

  return (
    <div className='referral-landing'>
      <img className='referral-landing-logo' src='/images/maayot-green-logo-small.svg' alt={BRAND} />
      <div className='referral-landing-gift' aria-hidden='true'>
        <span className='referral-landing-gift-emoji'>🎁</span>
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className={`referral-landing-spark referral-landing-spark-${i}`} />
        ))}
      </div>
      <h1 className='referral-landing-title'>{t('landing.title', { days: FRIEND_TRIAL_DAYS })}</h1>
      <p className='referral-landing-lead'>{t('landing.lead')}</p>
      <ul className='referral-landing-perks'>
        {PERKS.map((perk) => (
          <li key={perk.key}>
            <span className='referral-landing-perk-emoji' aria-hidden='true'>
              {perk.emoji}
            </span>
            <span>
              <Trans t={t} i18nKey={perk.key} components={{ strong: <strong /> }} />
            </span>
          </li>
        ))}
      </ul>
      <button type='button' className='referral-landing-btn' onClick={() => navigate(SIGNUP)}>
        {t('landing.cta', { days: FRIEND_TRIAL_DAYS })}
      </button>
      <p className='referral-landing-note'>
        {t('landing.note')} {t('common:teacherLanguageNotice')}
      </p>
    </div>
  )
}

export default ReferralLanding
